import $ from 'jquery';

import FactfinderProductList from './FactfinderProductList';
import FactfinderFacetFilter from './FactfinderFacetFilter';
import FactfinderSortSelect from './FactfinderSortSelect';
import FactfinderPagination from './FactfinderPagination';
import FactfinderCategoryTree from './FactfinderCategoryTree';
import FactfinderFacetOverview from "./FactfinderFacetOverview";
import FactfinderFeedback from "./FactfinderFeedback";
import FactfinderCategoryChips from "./FactfinderCategoryChips";

const EN_KO = window.EN_KO;

class FactfinderAftersearch {
	#lastResponse;

	constructor( containerEl ) {
		this.containerEl = containerEl;

		this.ProductList  = new FactfinderProductList( this.containerEl.querySelector( '.en_mdl_products' ) );
		this.FacetFilter  = new FactfinderFacetFilter( this.containerEl.querySelector( '.mdl_filter' ) );
		this.CategoryTree = new FactfinderCategoryTree( this.containerEl.querySelector( '.mdl_facet.mdl_facet--factfinder' ) )

		this.CategoryChips = new FactfinderCategoryChips( this.containerEl.querySelector( '.mdl_categeorychips' ) )

		this.SortSelect = new FactfinderSortSelect( this.containerEl.querySelector( '.mdl_section_header--listpage' ) );
		this.Pagination = new FactfinderPagination( this.containerEl.querySelector( '.mdl_pagination_list' ) );

		this.Overview = new FactfinderFacetOverview( this.containerEl.querySelector( '.mdl_filter__overview' ) );
		this.Feedback = new FactfinderFeedback( this.containerEl.querySelector( '.en_mdl_products' ) );

		this.containerEl.addEventListener( 'change.ff', ( ev ) => this.onChange( ev ) );
	}

	fetchData( params ) {
		const formEl = this.containerEl.querySelector( '.mdl_filter__form' );
		const channel = formEl.dataset.channel || window.FFChannel;

		let factfinderUrl = `/fact-finder/search/${ channel }/`;
		let headers = {};
		if ( document.location.host.endsWith( '.local' ) && localStorage.getItem( 'ff-custom-login-data' ) ) {
			factfinderUrl = `https://kastner-oehler.fact-finder.at/fact-finder/rest/v5/search/${ channel }/`;
			headers = {
				// You can set the header with:
				// localStorage.setItem( 'ff-custom-login-data', 'Basic ' + btoa( loginDataString ) )
				'Authorization': localStorage.getItem( 'ff-custom-login-data' ),
			};
		}

		const sid = EN_KO.functions.getFactfinderSession();

		return fetch( factfinderUrl, {
			method: 'POST',
			body: JSON.stringify( { params, sid } ),
			headers: {
				"Content-Type": "application/json",
				...headers
			},
		} )
			.then( async( res ) => {
				if ( res.ok ) {
					return await res.json()
				}

				return Promise.reject( await res.json() );
			} )
	}

	getParams( customParams ) {

		const allowedParamKeys = [
			'activeAbTests',
			'advisorStatus',
			'articleNumberSearch',
			'customParameters',
			'excludeProductsNotInRange',
			'filters',
			'followSearch',
			'hitsPerPage',
			'location',
			'marketIds',
			'maxCountVariants',
			'maxDistance',
			'page',
			'purchaserId',
			'query',
			'searchField',
			'showMarkets',
			'sortItems',
			'splitDocumentsAt'
		];

		return Object.fromEntries( allowedParamKeys.map( ( key ) => {
			const value = customParams[ key ] ?? null;
			if ( String( value ).startsWith( 'base64:' ) ) {
				try {
					return [ key, JSON.parse( atob( value.substring( 'base64:'.length ) ) ) ];
				} catch ( err ) {
					console.error( err ); // eslint-disable-line no-console
					return [ key, null ];
				}
			}

			return [ key, value ];
		} ).filter( param => !! param[1] ) );
	}

	saveParams( customParams ) {

		const orderedKeys = [
			'query',
			'page',
			'followSearch',
		].toReversed();

		const serializedParams = Object.entries( customParams ).map( ( [ key, value ] ) => {

			if ( 'object' === typeof value ) {
				if ( Array.isArray( value ) && value.length === 0 ) {
					return [ key, null ];
				}
				return [ key, 'base64:' + btoa( JSON.stringify( value ) ) ];
			}

			return [key, value];
		} ).toSorted( ( [keyA], [keyB] ) => {
			const indexA = orderedKeys.indexOf( keyA );
			const indexB = orderedKeys.indexOf( keyB );

			return indexB - indexA;
		} ).filter( ( param ) => !! param[1] );

		const urlParams = new URLSearchParams( Object.fromEntries( serializedParams ) );
		return history.replaceState( customParams, '', '?' + urlParams.toString() );
	}

	search( searchParams, mode = 'default' ) {

		const params = this.getParams( searchParams, mode );

		if ( 'overview' === mode ) {
			this.Overview.setLoading( true );
		} else {
			this.containerEl.classList.add( 'is-loading' );
			if ( 'initial' !== mode ) {
				this.saveParams( params );
			}
		}

		return this.fetchData( params )
			.then( async( responseData ) => {
				this.containerEl.classList.remove( 'has-error' );
				this.containerEl.classList.toggle( 'has-noresults', responseData.hits.length === 0 );

				// Append search query to heading.
				const heading = document.querySelector( '.mdl_listpageheader__heading' );
				if ( heading ) {
					heading.textContent = `Suche "${responseData.searchParams.query}"`;
				}

				if ( 'overview' === mode ) {
					await this.update( responseData, mode );
				} else {
					if ( ! this.redirect( responseData ) ) {
						await this.update( responseData, mode );
					}
				}

				this.#lastResponse = responseData;
				return responseData;
			} )
			.catch( data => {
				this.containerEl.classList.add( 'has-error' );
				console.error( data ); // eslint-disable-line no-console
			} )
			.finally( () => {
				if ( 'overview' === mode ) {
					this.Overview.setLoading( false );
				} else {
					this.containerEl.classList.remove( 'is-loading' );
				}
			} );
	}

	update( responseData, mode = 'default' ) {

		const Elements = [
			this.ProductList,
			this.SortSelect,
			this.FacetFilter,
			this.CategoryTree,
			this.CategoryChips,
			this.Pagination,
			this.Overview,
		];

		// Update all the data, then fire update callback.
		return Promise.resolve().then( () => {
			return Elements.reduce( ( data, Element ) => {
				if ( 'function' === typeof Element.manipulateResponse ) {
					return Element.manipulateResponse( data );
				}

				return data;
			}, responseData );
		} ).then( updatedResponseData => {
			if ( 'overview' === mode ) {
				return this.Overview.updateData( updatedResponseData ).then( () => {
					this.containerEl.dispatchEvent( new CustomEvent( 'update_overlay.ff', { bubbles: true, detail: { data: updatedResponseData } } ) );
				} );
			} else {
				return Promise.all(
					Elements.map( Element => Element.update( updatedResponseData ) )
				).then( () => {
					this.containerEl.dispatchEvent( new CustomEvent( 'update.ff', { bubbles: true, detail: { data: updatedResponseData } } ) );
				} );
			}
		} );
	}

	redirect( {campaigns, articleNumberSearch, hits, searchParams} ) {

		// We hit a Redirect campaign -> follow to destination.
		if ( campaigns ) {
			campaigns.forEach( campaign => {
				if ( 'REDIRECT' === campaign.flavour && campaign.target.destination ) {
					window.location.href = campaign.target.destination;
					return true;
				}
			} )
		}

		// Search for ArticleNumber, SKU or EAN -> Redirect to the product via SKU.
		if ( articleNumberSearch ) {
			// Redirect to the First Product, if any of the ProductIDs match our search.
			for ( const hit of hits ) {
				const {masterValues, variantValues} = hit;
				const variant = variantValues && variantValues.find( ( variant ) => !!variant );
				if ( masterValues && variant && [masterValues?.Artikelnummer, variant?.ProductID, variant?.Ean].includes( searchParams.query ) ) {
					window.location.href = variant?.ProductURL;
					return true;
				}
			}
		}

		return false;
	}

	onChange( ev ) {
		return this.search( ev.detail.searchParams, this.Overview.isOpen ? 'overview' : 'default' ).then( () => {
			window.scrollTo( { top:0, behavior: 'smooth' } )
		} );
	}

	get lastResponse() {
		return this.#lastResponse;
	}
}

const initFactfinderAftersearch = function( scopeEl = document, search = document.location.search ) {
	const params = new URLSearchParams( search );
	const containerEls = scopeEl.querySelectorAll( '.mdl_factfinder_aftersearch' );

	containerEls.forEach( containerEl => {
		containerEl.ACL_FACTFINDER_AFTERSEARCH ??= new FactfinderAftersearch( containerEl );
		containerEl.ACL_FACTFINDER_AFTERSEARCH.search( Object.fromEntries( params.entries() ), 'initial' );
	} );
};

$( document ).ready( function() {

	switch ( window.v_cl ) {
		case 'search':
			initFactfinderAftersearch();
			break;
	}

	// Update Title, when FF Updates.
	document.addEventListener( 'update.ff', ( ev ) => {
		const {data} = ev.detail;

		// Set Document Title.
		document.title = `${data.totalHits} Treffer für "${data.searchParams.query}"`;

		// Set Article Count in Filter Overlay.
		const mobileArticleCount = document.getElementById( 'available-articles-mobile-filter' );
		if ( mobileArticleCount ) {
			mobileArticleCount.innerHTML = data.totalHits;
		}

		// Set Article Count in Headline.
		const desktopArticleCount = document.getElementById( 'available-articles-filter' );
		if ( desktopArticleCount ) {
			desktopArticleCount.innerHTML = data.totalHits + ' Artikel';
		}
	} )
} )
